import React, { useMemo } from 'react';

import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import FormattedCurrency from '../../../../../components/formatted-currency';

import { paymentStatusOptions } from '../../data/payment-status-options';
import { paymentTerms as paymentTermsOptions } from '../../data/payment-terms-options';
import { PurchaseOrderSectionId } from '../../purchase-order-expandable-section-provider';
import { ExpandableSection } from '../expandable-section';
import { messages as editDetailMessages } from '../edit/purchase-order-details-messages';
import { messages as editPaymentMessages } from '../edit/purchase-order-payment-details-messages';
import { paymentDetailsMessages as previewMessages } from './payment-details.messages';
import styles from './shipping-details.styles.module.less';

type PaymentDetailsProps = {
  purchaseOrder: {
    currencyCode?: string;
    paymentTerms?: string;
    paymentStatus?: string;
    noteToSupplier?: string;
    shippingCost?: number;
    taxes?: number;
    tariffs?: number;
  };
};

export const PaymentDetails = ({ purchaseOrder }: PaymentDetailsProps) => {
  const { currencyCode, paymentTerms, paymentStatus, noteToSupplier, shippingCost = 0, taxes = 0, tariffs = 0 } = purchaseOrder;

  const paymentTermName = useMemo(() => {
    if (!paymentTerms) return '';
    const term = paymentTermsOptions.find((t) => t.key === paymentTerms);
    return term?.name ?? '';
  }, [paymentTerms]);

  const paymentStatusName = useMemo(() => {
    if (!paymentStatus) return '';
    const status = paymentStatusOptions.find((s) => s.key === paymentStatus);
    return status?.name ?? '';
  }, [paymentStatus]);

  const headerContent = <Text textType={TextType.H4} value={editPaymentMessages.paymentDetails} />;

  const paymentDetailsContent = (
    <FlexRow className={styles['details-grid']}>
      <FlexColumn className={styles['details-column']}>
        <Text className={styles.label} textType={TextType.Label} value={editDetailMessages.currency} />
        <Text className={styles.value} textType={TextType.Paragraph} value={currencyCode || '-'} />
        <Text className={styles.label} textType={TextType.Label} value={editDetailMessages.paymentTerms} />
        <Text className={styles.value} textType={TextType.Paragraph} value={paymentTermName || '-'} />
        <Text className={styles.label} textType={TextType.Label} value={previewMessages.notes} />
        <Text className={styles.value} textType={TextType.Paragraph} value={noteToSupplier || '-'} />
      </FlexColumn>
      <FlexColumn className={styles['details-column']}>
        <Text className={styles.label} textType={TextType.Label} value={editDetailMessages.paymentStatus} />
        <Text className={styles.value} textType={TextType.Paragraph} value={paymentStatusName || '-'} />
        <Text className={styles.label} textType={TextType.Label} value={editPaymentMessages.shippingCost} />
        <Text
          className={styles.value}
          textType={TextType.Paragraph}
          value={<FormattedCurrency currencyCode={currencyCode} value={shippingCost} />}
        />
        <Text className={styles.label} textType={TextType.Label} value={editPaymentMessages.taxes} />
        <Text
          className={styles.value}
          textType={TextType.Paragraph}
          value={<FormattedCurrency currencyCode={currencyCode} value={taxes} />}
        />
        <Text className={styles.label} textType={TextType.Label} value={editPaymentMessages.tariffs} />
        <Text
          className={styles.value}
          textType={TextType.Paragraph}
          value={<FormattedCurrency currencyCode={currencyCode} value={tariffs} />}
        />
      </FlexColumn>
    </FlexRow>
  );

  return (
    // @ts-expect-error ExpandableSection is untyped JS with optional props inferred as required
    <ExpandableSection
      ariaLabelMessageDescriptor={editPaymentMessages.paymentDetailsSection}
      className={styles['shipping-details-wrapper']}
      contentClassName={styles['expandable-content-container']}
      headerClassName={styles['expandable-header-container']}
      headerContent={headerContent}
      sectionContent={paymentDetailsContent}
      sectionId={PurchaseOrderSectionId.PaymentDetails}
    />
  );
};
